/**
 * Reordering API Service
 * Handles reordering of polls and text fields within a channel
 */

import { Http } from "@Services";

/**
 * Reorder polls within a channel
 * @param channelId - The channel ID
 * @param pollIds - Array of poll IDs in desired order
 * @throws Error if reordering fails
 */
export const reorderChannelPolls = async (
    channelId: string,
    pollIds: string[]
): Promise<void> => {
    try {
        await Http.Request<void>({
            method: "PATCH",
            url: `/groups/${channelId}/polls/order`,
            data: {
                poll_ids: pollIds,
            },
        });
    } catch (error) {
        throw new Error(`Failed to reorder polls: ${error}`);
    }
};

/**
 * Reorder text fields within a channel
 * @param channelId - The channel ID
 * @param order - Array of textfield IDs in desired order
 * @throws Error if reordering fails
 */
export const reorderChannelTextFields = async (
    channelId: string,
    order: string[]
): Promise<void> => {
    try {
        await Http.Request<void>({
            method: "PATCH",
            url: `/poll-text-fields/order`,
            data: {
                channel_id: channelId,
                order,
            },
        });
    } catch (error) {
        throw new Error(`Failed to reorder text fields: ${error}`);
    }
};

/**
 * Reorder both polls and text fields together as a unified list
 * @param channelId - The channel ID
 * @param orderedItems - Array of items with type and ID in desired order
 * @throws Error if reordering fails
 */
export const reorderChannelComponents = async (
    channelId: string,
    orderedItems: Array<{ type: "poll" | "textfield"; id: string }>
): Promise<void> => {
    // Validate that we have items to reorder
    if (!Array.isArray(orderedItems) || orderedItems.length === 0) {
        throw new Error("No items to reorder");
    }

    try {
        await Http.Request<void>({
            method: "PATCH",
            url: `/groups/${channelId}/components/order`,
            data: {
                items: orderedItems,
            },
        });
    } catch (error) {
        throw new Error(`Failed to reorder components: ${error}`);
    }
};
